import { BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, Link, useBCMSContext, useBCMSModal, useModel, useStateManager } from "@bcm-dev/bcms-vite";
import CustomTableAccordion, { CustomTableContent, CustomTableToggle, useCustomTableAccordion } from 'components/custom-ui/CustomTableAccordion';
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView, TableColumnContainer, TableColumnView, TableItemTextView, TableItemView, TableView } from 'components/shared';
import { CheckmarkBanner } from 'components/shared/views/ConfirmBannerView';
import CustomContentHeaderWidget from 'components/shared/views/CustomContentHeaderWidget';
import { SecondaryButton } from 'components/ui';
import Classes, { UnlinkedAccount } from "model";
import React, { useEffect } from 'react';
import { SVGAsset } from 'res';
import MobileUnlinkedAccountsTable from './components/MobileUnlinkedAccountsTable';
import './mobile.css';
import './styles.css';
import ManageRecipientAccountsPage from "../ManageRecipientAccountsPage";

/**
 * @function UnlinkedTransferAccountsPage
 * @return {React.Component}
 * @exported true
 * @description The page that shows the unlinked transfer accounts
 */

export default function UnlinkedTransferAccountsPage(props) {
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);

  const data = { ...getDefaultData(context), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };
  const className = props.className?(" "+props.className):"";

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      onAddAccountModalButtonClick: (newUnlinkedAcc) => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "accounts" ], [ newUnlinkedAcc, ...state.data.accounts ])
        .update([ "data", "addedUnlinkedAccount" ], newUnlinkedAcc)
        .update([ "ui", "successfullyAddedUnlinkedAccount", "isActive"], true)
        .submit(setState);
      },

      onAddUnlinkedTransferAccountModalClose: () => {
          BCMSStateManagerCRUD.init(state)
          .update([ "ui", "successfullyAddedUnlinkedAccount", "isActive"], false)
          .submit(setState);
      },

      onAddAccountPageButtonClick: () => {
          BCMSStateManagerCRUD.init(state)
          .update([ "ui", "successfullyAddedUnlinkedAccount", "isActive"], false)
          .submit(setState);
      },
      
  });

  return(
    <PageContainer className={"unlinked-transfer-accounts-page"+className} state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </PageContainer>
  );
}

export const getDefaultData = (controller, _fiName, _accounts) => {
  const accounts = _accounts ?? UnlinkedAccount.initAll().sort((a, b) => b.id - a.id);
  const fiName = _fiName ?? controller.state.settings.fiName;

  return {
    accounts,
    fiName: fiName,
    addedUnlinkedAccount: undefined
  };
}

export const getDefaultUI = () => ({
  successfullyAddedUnlinkedAccount: { isActive: false },
  addUnlinkedTransferAccountModal: { isActive: false, step: undefined, form: undefined },
});

function PageContent(props) {
  const { data, ui, onAddAccountPageButtonClick, onAddUnlinkedTransferAccountModalClose } = useStateManager();
  const { open } = useBCMSModal();
  const accounts = data.accounts ?? [];
  const { branding } = useModel();
  const { options } = branding;
  const subHeaderContent = options.transfer_accounts_subheader_abi22;
  const promoText = options.transfer_accounts_promo_abi22;
  const subfooterText = options.transferaaccounts_subfooter_abi22;
  const onAddAccountClick = () => {
    onAddAccountPageButtonClick();
    open("AddUnlinkedTransferAccountModal");
  }

  useEffect(() => {
      if(ui.addUnlinkedTransferAccountModal.isActive) {
        const modalData = { 
          step: ui.addUnlinkedTransferAccountModal.step,
          form: ui.addUnlinkedTransferAccountModal.form
        };
        open("AddUnlinkedTransferAccountModal", modalData);
      }
  } , [ui]);

  return (
    <PageContentView>
      <div className="flex-container flex-vertical page-widget-wrapper">
        {promoText && <CustomContentHeaderWidget content = {promoText}/>}
        { ui.successfullyAddedUnlinkedAccount.isActive
        && <CheckmarkBanner text={ `"${ data.addedUnlinkedAccount.nickname }" has been added as an unlinked account. ` }
         dataPoint="" onClick={ onAddUnlinkedTransferAccountModalClose } /> }

        <CenterContentContainer>
          <div className="upper-content">
            <ContentHeaderView text="Transfer Accounts" />
            {subHeaderContent && <CustomContentHeaderWidget content = {subHeaderContent}/>}
            <SecondaryButton text="Add Account" dataPoint="Add_Account_Button" onClick={ onAddAccountClick } />
            <h1>Unlinked { data.fiName } Accounts</h1>
          </div>
          <div className="show-on-desktop">
            <UnlinkedAccountsTable accounts={ accounts } />
          </div>
          <div className="show-on-mobile">
            <MobileUnlinkedAccountsTable accounts={ accounts } />
          </div>
        </CenterContentContainer>
        { subfooterText && <CustomContentHeaderWidget content={ subfooterText } /> }
      </div>
    </PageContentView>
  );
}

/**
 * @function UnlinkedAccountsTable
 * @return {React.Component}
 * @exported true
 * @description The unlinked accounts table
 */

function UnlinkedAccountsTable(props) {
  const accounts = props.accounts ?? [];
  const rows = accounts.reduce((acc, account) => {
    acc.push(
      <UnlinkedAccountsTableRow key={ account.id } account={ account } />
    );
    return acc;
  }, []);

  return(
    <TableView className="unlinked-accounts-table">
      <TableColumnContainer>
        <TableColumnView text="" /> 
        <TableColumnView text="Nickname" icon="ArrowUpIcon" />
        <TableColumnView text="Account Owner" icon="SortIcon" />
        <TableColumnView text="Account Owner Type" icon="SortIcon" />
        <TableColumnView text="Account Number" />
        <TableColumnView text="Date Added" icon="SortIcon" />
      </TableColumnContainer>
      { rows }
    </TableView>
  );
}

/**
 * @function UnlinkedAccountsTableRow
 * @return {React.Component}
 * @exported false
 * @description The table row in the unlinked account table
 */

function UnlinkedAccountsTableRow(props) {
  const account = props.account ?? {};
  const activeRow = account.activeRow ?? false;

  return(
      <CustomTableAccordion activeClass="unlinked-accounts-table-row-open" state={ activeRow }>
        <UnlinkedAccountsTableToggle account={ account } />
        <UnlinkedAccountsTableToggleContent account={ account } />
      </CustomTableAccordion>
  );
}

/**
 * @function UnlinkedAccountsTableToggle
 * @return {React.Component}
 * @exported false
 * @description description
 */

function UnlinkedAccountsTableToggle(props) {
  const { state } = useCustomTableAccordion();
  const account = props.account ?? {};
  const accountOwner = account.accountOwnerType.toLowerCase() ==="personal" ? `${ account.accountOwnerFirstName } ${ account.accountOwnerLastName }`: account.accountOwner;

  return(
    <CustomTableToggle>
      <tr>
        <TableItemView className="expand-row-chevron">
          <SVGAsset id={ state ? "DownChevronIcon" : "RightChevronIcon"} data-point={ "Transfer_Chevron_" + account.id } />
        </TableItemView>
        <TableItemTextView text={ account.nickname } />
        <TableItemTextView text={accountOwner } />
        <TableItemTextView className="account-owner-type-column" text={ account.accountOwnerType } />
        <TableItemTextView text={ account.number } />
        <TableItemTextView text={ account.dateAdded.format("m/d/Y") } />
      </tr>
    </CustomTableToggle>
  );
}

/**
 * @function UnlinkedAccountsTableToggleContent
 * @return {React.Component}
 * @exported false
 * @description The toggled content in the unlinked accounts table
 */

function UnlinkedAccountsTableToggleContent(props) {
  const account = props.account ?? {};

  return(
    <CustomTableContent>
    <tr>
        <td className="scheduled-transfers-table-item" colSpan="6">
            <div className="flex-container">
                <p className="flex-width">ACCOUNT DETAILS</p>         
                <CustomTableToggle>
                    <SVGAsset id="CloseX" className="set-width clickable" />
                </CustomTableToggle>          
            </div>

            <FormContainer>
                <FormRowView text="Date Added">
                    <p>{ account.dateOpened.format("m/d/Y") }</p>
                </FormRowView>
                
                <FormRowView className="link-container">
                    <DisabledPrompt>
                        <Link>
                          <span data-point={ `Unlinked_Transfer_Accounts_Edit_Account_Link_${ account.id }` }>Edit Account</span>
                        </Link>
                    </DisabledPrompt>
                </FormRowView>
                
                <FormRowView className="link-container">
                    <DisabledPrompt>
                        <Link>
                          <span data-point={ `Unlinked_Transfer_Accounts_Remove_Account_Link_${ account.id }` }>Remove Account</span>
                        </Link>
                    </DisabledPrompt>
                </FormRowView>
            </FormContainer>
        </td>
    </tr>
</CustomTableContent>
  );
}

